// ─────────────────────────────────────────────────────────────────────────
// Retired routes: they land on the homepage.
//
// Tom retired these on 5 October 2026 with the new direction
// (docs/decisions/2026-10-05-bloke-in-a-shed-content-engines.md):
//
//   /challenge/brain-starter             its Brain Starter prompt page
//   /brain-starter                       the short form of the same address
//   /score                               the AI-Native Score assessment
//   ?view=brain-starter|score            their local-dev aliases
//
// The challenge waitlist is restored separately (owner decision, 23 October).
// The other retired pages stay in the repo as history.
// vercel.json redirects the same addresses at the edge (keep the two lists in
// step; retiredRoutes.test.ts holds them together). This client-side redirect
// covers local development, the ?view= aliases and any in-app navigation.
//
// Only the bare addresses retire: `public/challenge/covers/`, `sales/` and
// `course/` are static asset folders the member area still uses.
// ─────────────────────────────────────────────────────────────────────────

import { isBrainStarterPath } from './brainStarterRoute';

/** Bare paths that remain retired. */
export const RETIRED_PATHS = ['/score', '/brain-starter', '/challenge/brain-starter'] as const;

/** Cheap and total: called before the marketing surface renders anything. */
export function isRetiredPath(pathname: string, search = ''): boolean {
  const path = pathname.replace(/\/+$/, '') || '/';
  if ((RETIRED_PATHS as readonly string[]).includes(path)) return true;
  if (isBrainStarterPath(path, search)) return true;
  return new URLSearchParams(search).get('view') === 'score';
}

/**
 * Where a retired address goes: the homepage, keeping only campaign tags
 * (utm_*) so a link already out in the world still attributes its visit.
 */
export function retiredRouteTarget(search = ''): string {
  const kept = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (key.toLowerCase().startsWith('utm_')) kept.append(key, value);
  });
  const query = kept.toString();
  return query ? `/?${query}` : '/';
}

/**
 * If the current address is retired, swap it for the homepage in place
 * (replaceState, so Back does not bounce the visitor into it again).
 * Returns the pathname the app should render.
 */
export function redirectRetiredRoute(loc: Pick<Location, 'pathname' | 'search'> = window.location, history: Pick<History, 'replaceState'> = window.history): string {
  if (!isRetiredPath(loc.pathname, loc.search)) return loc.pathname;
  history.replaceState(null, '', retiredRouteTarget(loc.search));
  return '/';
}
